import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import CallbackDialog from "@/components/CallbackDialog";
import EnquiryForm from "@/components/EnquiryForm";
import { CheckCircle2, ShoppingCart, PhoneCall, Zap, ShieldCheck, Wrench, ArrowRight } from "lucide-react";

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [subscription, setSub] = useState("yearly");
  const [installation, setInstall] = useState(true);
  const [vehicleType, setVehicleType] = useState("");
  const [cbOpen, setCbOpen] = useState(false);
  const { addItem } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => {});
  }, [slug]);

  if (!p) return <div className="max-w-7xl mx-auto p-14">Loading…</div>;

  const hasPrice = p.price > 0;
  const subAmount = subscription === "monthly" ? p.monthly_subscription : subscription === "yearly" ? p.yearly_subscription : 0;
  const installAmount = installation ? p.installation_charge || 0 : 0;
  const lineTotal = hasPrice ? p.price * qty + installAmount * qty + subAmount * qty : 0;

  const doAdd = (goToCart) => {
    if (!hasPrice) {
      setCbOpen(true);
      return;
    }
    addItem(p, { quantity: qty, subscription, installation, vehicle_type: vehicleType });
    toast.success("Added to cart");
    if (goToCart) nav("/cart");
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="product-detail-page">
      <div className="text-xs text-slate-500 mb-6">
        <Link to="/products" className="hover:text-[#FF6D00]">Products</Link> / {p.name}
      </div>

      <div className="grid lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-slate-100 border border-slate-200">
            <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
          </div>
          {p.gallery && p.gallery.length > 0 && (
            <div className="mt-4 grid grid-cols-4 gap-3">
              {p.gallery.map((g, i) => (
                <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                  <img src={g} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="eyebrow">{p.category}</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A192F] mt-2">{p.name}</h1>
          <p className="mt-4 text-slate-600">{p.description}</p>

          <div className="mt-6 flex items-end gap-4">
            {hasPrice ? (
              <>
                <div className="font-heading text-4xl font-bold text-[#0A192F]">{fmtINR(p.price)}</div>
                {p.mrp && p.mrp > p.price && (
                  <div className="pb-1 text-slate-500 line-through">{fmtINR(p.mrp)}</div>
                )}
              </>
            ) : (
              <div className="font-heading text-3xl font-bold text-[#FF6D00]">Request a Quote</div>
            )}
          </div>

          {hasPrice && (
            <>
              <div className="mt-8 space-y-5">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Subscription plan</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "none", label: "No plan" },
                      { key: "monthly", label: `Monthly · ${fmtINR(p.monthly_subscription || 0)}` },
                      { key: "yearly", label: `Yearly · ${fmtINR(p.yearly_subscription || 0)}` },
                    ].map((opt) => (
                      <button key={opt.key} onClick={() => setSub(opt.key)} data-testid={`sub-${opt.key}`}
                        className={`text-xs px-3 py-2 rounded-lg border transition-colors ${subscription === opt.key ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-[#0A192F]"}`}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-6">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Quantity</div>
                    <div className="inline-flex items-center rounded-lg border border-slate-200 overflow-hidden">
                      <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-10 h-10 hover:bg-slate-50">−</button>
                      <span className="w-10 h-10 flex items-center justify-center font-semibold" data-testid="qty-value">{qty}</span>
                      <button onClick={() => setQty(qty + 1)} className="w-10 h-10 hover:bg-slate-50">+</button>
                    </div>
                  </div>
                  {p.installation_charge > 0 && (
                    <label className="flex items-center gap-2 text-sm text-slate-700 mt-6">
                      <input type="checkbox" checked={installation} onChange={(e) => setInstall(e.target.checked)} className="w-4 h-4 accent-[#FF6D00]" data-testid="install-toggle" />
                      Add installation ({fmtINR(p.installation_charge)})
                    </label>
                  )}
                </div>

                <div>
                  <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Vehicle type</div>
                  <select value={vehicleType} onChange={(e) => setVehicleType(e.target.value)} className="h-11 w-full max-w-xs px-3 rounded-lg border border-slate-200 bg-white" data-testid="vehicle-type">
                    <option value="">Select vehicle</option>
                    <option>Car / SUV</option>
                    <option>Two-Wheeler</option>
                    <option>Truck</option>
                    <option>School Bus</option>
                    <option>Other</option>
                  </select>
                </div>
              </div>

              <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-200 text-sm">
                <div className="flex justify-between"><span>Device × {qty}</span><span>{fmtINR(p.price * qty)}</span></div>
                {installation && <div className="flex justify-between text-slate-600"><span>Installation × {qty}</span><span>{fmtINR(installAmount * qty)}</span></div>}
                {subAmount > 0 && <div className="flex justify-between text-slate-600"><span>Subscription × {qty}</span><span>{fmtINR(subAmount * qty)}</span></div>}
                <div className="mt-2 pt-2 border-t border-slate-200 flex justify-between font-semibold text-[#0A192F]">
                  <span>Subtotal (before GST)</span><span>{fmtINR(lineTotal)}</span>
                </div>
              </div>
            </>
          )}

          <div className="mt-6 flex flex-wrap gap-3">
            {hasPrice ? (
              <>
                <button onClick={() => doAdd(false)} data-testid="pdp-add-cart" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#0A192F] text-white font-semibold hover:bg-[#112240] active:scale-95 transition-transform">
                  <ShoppingCart className="w-4 h-4" /> Add to Cart
                </button>
                <button onClick={() => doAdd(true)} data-testid="pdp-buy-now" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF6D00] text-white font-semibold hover:bg-[#ff8433] active:scale-95 transition-transform">
                  Buy Now <ArrowRight className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button onClick={() => setCbOpen(true)} data-testid="pdp-request-quote" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-[#FF6D00] text-white font-semibold hover:bg-[#ff8433] transition-colors">
                Request a Quote <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button onClick={() => setCbOpen(true)} data-testid="pdp-callback" className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-slate-200 text-slate-700 hover:border-[#FF6D00] hover:text-[#FF6D00] transition-colors">
              <PhoneCall className="w-4 h-4" /> Request Callback
            </button>
          </div>

          <div className="mt-6 flex flex-wrap gap-4 text-xs text-slate-600">
            <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00E676]" />{p.warranty}</div>
            <div className="flex items-center gap-2"><Wrench className="w-4 h-4 text-[#FF6D00]" />Professional installation</div>
            <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-[#2563EB]" />4G LTE + 2G fallback</div>
          </div>
        </div>
      </div>

      {/* Features */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl font-bold text-[#0A192F]">Key features</h2>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {p.features.map((f) => (
            <div key={f} className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white">
              <CheckCircle2 className="w-5 h-5 text-[#00E676] mt-0.5" />
              <span className="text-sm text-slate-700">{f}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-16 grid lg:grid-cols-2 gap-8">
        <EnquiryForm title="Have questions about this product?" subtitle="Our GPS specialists will reach out with recommendations." product_id={p.id} enquiry_type="quote" />
      </section>

      <CallbackDialog open={cbOpen} onOpenChange={setCbOpen} product={p} />
    </div>
  );
}
